(() => {
  const api = () => window.TechTicCompare;
  const strings = () => window.techticCompareStrings || {};

  function escapeHtml(value) {
    const element = document.createElement('div');
    element.textContent = value == null ? '' : String(value);
    return element.innerHTML;
  }

  function imageUrl(value) {
    if (!value) return '';
    return value.startsWith('//') ? `https:${value}` : value;
  }

  function formatMoney(cents) {
    const currency = window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'ZAR';
    const value = (Number(cents) || 0) / 100;
    if (currency === 'ZAR') {
      return `R ${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)}`;
    }
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2
    }).format(value);
  }

  function selectedVariant(product, item) {
    return product.variants.find((variant) => String(variant.id) === String(item.id))
      || product.variants.find((variant) => variant.available)
      || product.variants[0];
  }

  function optionText(product, variant) {
    if (!variant || !product.options || product.options.length === 0) return '-';
    return product.options
      .map((name, index) => `${name}: ${variant.options[index] || '-'}`)
      .filter((value) => !value.endsWith(': Default Title'))
      .join(', ') || '-';
  }

  function productCell(product, variant, item) {
    const available = Boolean(variant && variant.available);
    const source = imageUrl(product.featured_image || product.images[0]);
    const resizedSource = source ? `${source}${source.includes('?') ? '&' : '?'}width=500` : '';
    const image = source
      ? `<img class="tt-compare__product-image" src="${escapeHtml(resizedSource)}" alt="${escapeHtml(product.title)}" width="500" height="500" loading="lazy">`
      : '<div class="tt-compare__product-image"></div>';

    return `
      <article class="tt-compare__product">
        <a href="/products/${escapeHtml(product.handle)}">${image}</a>
        <a class="tt-compare__product-title" href="/products/${escapeHtml(product.handle)}">${escapeHtml(product.title)}</a>
        <div class="tt-compare__price">${formatMoney(variant ? variant.price : product.price)}</div>
        <div class="tt-compare__stock${available ? '' : ' is-unavailable'}">${available ? strings().inStock || 'In stock' : strings().soldOut || 'Sold out'}</div>
        <div class="tt-compare__product-actions">
          <button class="tt-compare__cart" type="button" data-tt-compare-cart="${escapeHtml(variant && variant.id)}"${available ? '' : ' disabled'}>${strings().addToCart || 'Add to cart'}</button>
          <button class="tt-compare__remove" type="button" data-tt-compare-remove="${escapeHtml(item.id)}" aria-label="${escapeHtml(strings().remove || 'Remove from compare')}">${strings().removeShort || 'Remove'}</button>
        </div>
      </article>`;
  }

  function valueCell(value) {
    return `<div class="tt-compare__value">${escapeHtml(value || '-')}</div>`;
  }

  class ComparePage extends HTMLElement {
    connectedCallback() {
      this.empty = this.querySelector('.compare-is-empty');
      this.main = this.querySelector('.compare-content');
      this.content = this.querySelector('.compare-ajax-content');
      this.loadId = 0;
      this.boundUpdate = () => this.render();
      this.boundStorage = (event) => {
        if (event.key === 'productDataCompare') this.render();
      };

      window.addEventListener('techticCompareUpdated', this.boundUpdate);
      window.addEventListener('storage', this.boundStorage);
      this.addEventListener('click', (event) => this.handleClick(event));
      this.render();
    }

    disconnectedCallback() {
      window.removeEventListener('techticCompareUpdated', this.boundUpdate);
      window.removeEventListener('storage', this.boundStorage);
    }

    show(element, visible) {
      if (element) element.classList.toggle('hide', !visible);
    }

    async render() {
      if (!api() || !this.content) return;
      const items = api().readItems();
      this.show(this.empty, items.length === 0);
      this.show(this.main, items.length > 0);

      if (items.length === 0) {
        this.content.innerHTML = '';
        return;
      }

      const currentLoad = ++this.loadId;
      this.content.innerHTML = `<div class="tt-compare-loading" role="status">${strings().loading || 'Loading products...'}</div>`;

      const results = await Promise.allSettled(items.map(async (item) => {
        const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`, { credentials: 'same-origin' });
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        const product = await response.json();
        return { item, product, variant: selectedVariant(product, item) };
      }));

      if (currentLoad !== this.loadId) return;
      const products = results
        .filter((result) => result.status === 'fulfilled')
        .map((result) => result.value);

      if (products.length === 0) {
        this.content.innerHTML = `<div class="tt-compare-error">${strings().loadError || 'Unable to load these products. Please try again.'}</div>`;
        return;
      }

      if (products.length !== items.length) {
        api().writeItems(products.map((entry) => entry.item));
        return;
      }

      const rows = [
        [strings().brand || 'Brand', products.map(({ product }) => product.vendor)],
        [strings().productType || 'Product type', products.map(({ product }) => product.type)],
        [strings().sku || 'SKU', products.map(({ variant }) => variant && variant.sku)],
        [strings().availability || 'Availability', products.map(({ variant }) => variant && variant.available ? strings().inStock || 'In stock' : strings().soldOut || 'Sold out')],
        [strings().options || 'Options', products.map(({ product, variant }) => optionText(product, variant))]
      ];

      this.content.innerHTML = `
        <div class="tt-compare" style="--tt-compare-count: ${products.length}">
          <div class="tt-compare__toolbar">
            <strong>${products.length} ${products.length === 1 ? strings().product || 'product' : strings().products || 'products'}</strong>
            <button class="tt-compare__clear" type="button" data-tt-compare-clear>${strings().clearAll || 'Clear all'}</button>
          </div>
          <div class="tt-compare__scroll" tabindex="0" aria-label="${escapeHtml(strings().comparisonTable || 'Product comparison table')}">
            <div class="tt-compare__grid">
              <div class="tt-compare__label">${escapeHtml(strings().product || 'Product')}</div>
              ${products.map(({ product, variant, item }) => productCell(product, variant, item)).join('')}
              ${rows.map(([label, values]) => `
                <div class="tt-compare__label">${escapeHtml(label)}</div>
                ${values.map((value) => valueCell(value)).join('')}`).join('')}
            </div>
          </div>
        </div>`;
    }

    async handleClick(event) {
      const cartButton = event.target.closest('[data-tt-compare-cart]');
      if (!cartButton || cartButton.disabled) return;

      cartButton.disabled = true;
      const originalText = cartButton.textContent;
      cartButton.textContent = strings().adding || 'Adding...';

      try {
        const response = await fetch(window.routes && window.routes.cart_add_url || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(cartButton.dataset.ttCompareCart), quantity: 1 }] })
        });
        if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
        window.location.assign(window.routes && window.routes.cart_url || '/cart');
      } catch (error) {
        cartButton.disabled = false;
        cartButton.textContent = strings().cartError || 'Try again';
        window.setTimeout(() => {
          cartButton.textContent = originalText;
        }, 1800);
      }
    }
  }

  if (!customElements.get('compare-page')) customElements.define('compare-page', ComparePage);
})();
